import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FeatherModule } from 'angular-feather';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { NotificationService } from 'src/app/Theme/layout/admin/notification.service';
import {
  NotificationGenericComponent,
  NotificationItem
} from '../notification-generic/notification-generic.component';
import { CustomToastService } from '../../service/toast.service';

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule, TranslateModule, FeatherModule, NotificationGenericComponent],
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.scss',
  host: {
    '[class.notification-bell-host--header]': 'placement === "header"',
    '[class.notification-bell-host--bottom-nav]': 'placement === "bottom-nav"'
  }
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  @Input() placement: 'header' | 'bottom-nav' = 'header';
  @Input() viewAllRoute: string | any[] = ['/tf/notifications'];

  @ViewChild('notificationBtn') notificationBtn!: ElementRef<HTMLButtonElement>;

  isOpen = false;
  popupList: NotificationItem[] = [];
  unreadCount = 0;

  private allNotifications: NotificationItem[] = [];
  private justMarkedAllAsRead = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private notificationService: NotificationService,
    private translate: TranslateService,
    private toastService: CustomToastService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadPopupNotifications();

    this.notificationService.getUnreadCount()
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.unreadCount = count;
        this.cdr.detectChanges();
      });

    this.notificationService.getRefreshRequested()
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => this.loadPopupNotifications());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggle(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const willOpen = !this.isOpen;
    this.isOpen = willOpen;
    if (willOpen) {
      this.loadPopupNotifications();
    }
  }

  close(): void {
    this.isOpen = false;
  }

  markAllAsRead(): void {
    this.justMarkedAllAsRead = true;
    this.popupList = [];
    this.allNotifications = [];
    this.notificationService.setUnreadCount(0);
    this.cdr.detectChanges();
    this.notificationService.markAllAsRead().subscribe({
      next: () => {
        this.notificationService.markPendingNotificationsAsRead();
        this.loadPopupNotifications();
      },
      error: () => {
        this.justMarkedAllAsRead = false;
        this.cdr.detectChanges();
      }
    });
  }

  onNotificationClick(notification: NotificationItem): void {
    const pk = this.getNotificationPk(notification);
    if (pk != null) {
      this.notificationService.markAsRead(pk).subscribe({
        next: () => {
          this.applyNotificationReadLocally(notification);
          const msg = this.translate.instant('TFNotification.MarkAsReadToast');
          this.toastService.showSuccess(msg, '');
        },
        error: () => {
          this.toastService.showError(
            this.translate.instant('TFNotification.MarkAsReadError'),
            ''
          );
        }
      });
      return;
    }

    this.applyNotificationReadLocally(notification);
    const msg = this.translate.instant('TFNotification.MarkAsReadToast');
    this.toastService.showSuccess(msg, '');
  }

  private loadPopupNotifications(): void {
    this.notificationService.getNotif().subscribe({
      next: (res) => {
        const rawList = Array.isArray(res) ? res : (res && res.results) ? res.results : [];
        const httpMapped = rawList.map((n: any) => this.notificationService.normalizeForDisplay(n));
        const wsMapped = this.notificationService.getNotificationsSnapshot()
          .map((n: any) => this.notificationService.normalizeForDisplay(n));
        const merged = this.mergeNotificationLists(wsMapped, httpMapped);
        const sorted = this.sortNotificationsForPopup(merged);
        const todayList = sorted.filter((n: any) => this.isCurrentDay(n));
        this.justMarkedAllAsRead = false;
        this.recomputeUnreadAggregates(sorted);

        const listUnchanged =
          this.areNotificationListsEqual(this.allNotifications, sorted) &&
          this.areNotificationListsEqual(this.popupList, todayList);
        if (listUnchanged) {
          return;
        }

        this.allNotifications = sorted;
        this.popupList = todayList;
        this.cdr.detectChanges();
      },
      error: () => {
        this.justMarkedAllAsRead = false;
        this.popupList = [];
        this.allNotifications = [];
        this.notificationService.setUnreadCount(0);
        this.cdr.detectChanges();
      }
    });
  }

  private mergeNotificationLists(wsItems: any[], httpItems: any[]): any[] {
    const byKey = new Map<string, any>();
    const put = (item: any) => {
      const id = item?.id ?? item?.message?.id;
      const key = id != null ? `id:${id}` : `tmp:${item?.title}:${item?.message}:${item?.created_at}`;
      byKey.set(key, item);
    };
    httpItems.forEach(put);
    wsItems.forEach(put);
    return Array.from(byKey.values());
  }

  private sortNotificationsForPopup(items: any[]): any[] {
    return [...items].sort((a: any, b: any) => {
      const aRead = !!a?.read;
      const bRead = !!b?.read;
      if (aRead !== bRead) {
        return aRead ? 1 : -1;
      }
      const aTime = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return bTime - aTime;
    });
  }

  private recomputeUnreadAggregates(sourceList: any[] = this.popupList): void {
    const unreadTotal = (sourceList || []).filter((n: any) => this.isUnread(n)).length;
    this.notificationService.setUnreadCount(unreadTotal);
  }

  private isCurrentDay(notification: any): boolean {
    const value = notification?.created_at ?? notification?.date ?? notification?.timestamp;
    if (value == null || value === '') {
      return false;
    }
    const notificationDate = new Date(value);
    if (isNaN(notificationDate.getTime())) {
      return false;
    }
    return notificationDate.toDateString() === new Date().toDateString();
  }

  private areNotificationListsEqual(a: NotificationItem[] = [], b: NotificationItem[] = []): boolean {
    if (a.length !== b.length) {
      return false;
    }
    return a.every((item, index) => {
      const other = b[index];
      return this.getNotificationPk(item) === this.getNotificationPk(other)
        && !!item.read === !!other.read
        && (item.title || '') === (other.title || '')
        && String(item.message || '') === String(other.message || '')
        && String(item.created_at || '') === String(other.created_at || '');
    });
  }

  private isUnread(n: any): boolean {
    if (!n) {
      return true;
    }
    const read = n.read ?? n.is_read;
    return read !== true && read !== 'true';
  }

  private getNotificationPk(notification: NotificationItem): number | string | null | undefined {
    const directId = notification['id'];
    if (directId != null) {
      return directId as number | string;
    }

    const message = notification['message'];
    if (message != null && typeof message === 'object' && !Array.isArray(message)) {
      const messageId = (message as Record<string, unknown>)['id'];
      return messageId as number | string | null | undefined;
    }

    return undefined;
  }

  private applyNotificationReadLocally(notification: NotificationItem): void {
    notification.read = true;
    const message = notification['message'];
    if (message != null && typeof message === 'object' && !Array.isArray(message)) {
      (message as { read?: boolean }).read = true;
    }
    this.popupList = this.sortNotificationsForPopup(this.popupList || []);
    this.allNotifications = this.sortNotificationsForPopup(this.allNotifications || []);
    this.recomputeUnreadAggregates(this.allNotifications);
    this.cdr.detectChanges();
  }
}
